<template>
  <div class="tags-form">
    <div class="form-row">
      <div
        v-for="definition in sortedDefinitions"
        :key="definition.id"
        :class="['form-group', getColSize(definition)]"
      >
        <label>
          {{ definition.name }}
          <small v-if="definition.description" class="text-muted d-block">
            {{ definition.description }}
          </small>
        </label>
        <TagInput
          :definition="definition"
          :value="localTags[definition.code]"
          :error="errors[definition.code]"
          @input="handleTagChange(definition.code, $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TagInput from './TagInput.vue'
import {validateTags} from '../utils/tagValidation'

export default {
  name: 'TagsForm',
  components: {
    TagInput
  },
  props: {
    definitions: {
      type: Array,
      required: true
    },
    tags: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      localTags: {},
      errors: {}
    }
  },
  computed: {
    sortedDefinitions() {
      return [...this.definitions].sort((a, b) => a.order - b.order)
    }
  },
  watch: {
    tags: {
      immediate: true,
      deep: true,
      handler(newTags) {
        // Initialize local tags with values from props
        this.localTags = {}
        for (const definition of this.definitions) {
          // Handle structured tags format (tags.media, tags.face, etc.)
          const value = newTags[definition.code]

          if (value !== undefined && value !== null) {
            this.localTags[definition.code] = value
          } else {
            // Initialize with empty value based on input type
            this.localTags[definition.code] = this.getEmptyValue(definition.input_type)
          }
        }
      }
    },
    definitions: {
      immediate: true,
      handler() {
        // Re-initialize when definitions change
        this.initializeLocalTags()
      }
    }
  },
  methods: {
    initializeLocalTags() {
      this.localTags = {}
      for (const definition of this.definitions) {
        const value = this.tags[definition.code]
        if (value !== undefined && value !== null) {
          this.localTags[definition.code] = value
        } else {
          this.localTags[definition.code] = this.getEmptyValue(definition.input_type)
        }
      }
    },
    getEmptyValue(inputType) {
      switch (inputType) {
        case 'multiselect':
        case 'checkbox':
        case 'tags':
          return []
        case 'number':
          return ''
        case 'select':
          return ''
        case 'date':
          return ''
        case 'text':
        default:
          return ''
      }
    },
    getColSize() {
      return 'col-12'
    },
    handleTagChange(code, value) {
      this.localTags[code] = value

      // Clear error for this field when user changes it
      if (this.errors[code]) {
        this.$delete(this.errors, code)
      }

      this.$emit('change', {...this.localTags})
    },
    validate() {
      this.errors = validateTags(this.localTags, this.definitions)
      return Object.keys(this.errors).length === 0
    },
    getData() {
      // Cleared values are sent as null: the backend removes the tag,
      // while omitted keys would keep their current value
      const data = {}
      for (const [code, value] of Object.entries(this.localTags)) {
        const isCleared = value === '' || value === null || value === undefined ||
          (Array.isArray(value) && value.length === 0)
        data[code] = isCleared ? null : value
      }
      return data
    }
  }
}
</script>
